import { Button } from '@mantine/core';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch';
import { DateInput } from '@mantine/dates';
import { NumberInput } from 'react-hook-form-mantine';
import { useWatch } from 'react-hook-form';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import type { ProgrammeEmailScheduleConfigRes } from '@dealstack/shared/lib/backendClient';
import {
	StyledConfigCard,
	ConfigCardHeader,
	ConfigCardTitle,
	ConfigCardDescription,
	CadenceFieldsRow,
	CadenceText,
	PreviewLine,
	ConfigCardFooter,
} from './styles';
import { CONFIG_CARD_FORM_FIELDS } from './ConfigCard.types';
import type { ConfigCardOnSave } from './ConfigCard.types';
import { useConfigCardForm } from './useConfigCardForm';
import { CONFIG_TYPE_LABEL_KEYS, buildConfigPreviewLine } from './ConfigCard.utils';

interface Props {
	config: ProgrammeEmailScheduleConfigRes;
	onSave: (values: ConfigCardOnSave) => void;
	isSaving: boolean;
	showEndDate?: boolean;
}

export const ConfigCard = ({ config, onSave, isSaving, showEndDate = true }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { control, handleSubmit, setValue } = useConfigCardForm(config);
	const labelKeys = CONFIG_TYPE_LABEL_KEYS[config.type];

	const formValues = useWatch({ control });
	const enabled = formValues.enabled ?? config.enabled;
	const endDate = formValues.endDate ?? (config.endDate ? new Date(config.endDate) : null);
	const previewLine = buildConfigPreviewLine({
		enabled,
		initialDelayDays: formValues.initialDelayDays ?? config.initialDelayDays,
		intervalDays: formValues.intervalDays ?? config.intervalDays,
		maxFires: formValues.maxFires ?? config.maxFires,
	});

	const onSubmit = handleSubmit((values) => {
		onSave({ type: config.type, ...values });
	});

	return (
		<StyledConfigCard>
			<ConfigCardHeader>
				<div>
					<ConfigCardTitle>{tMipManage(labelKeys.title)}</ConfigCardTitle>
					<ConfigCardDescription>
						{tMipManage(labelKeys.description)}
					</ConfigCardDescription>
				</div>
				<AdvancedSwitch
					selectedValue={enabled}
					onChange={(value) =>
						setValue(CONFIG_CARD_FORM_FIELDS.enabled, !!value, {
							shouldDirty: true,
						})
					}
				/>
			</ConfigCardHeader>

			<CadenceFieldsRow>
				<CadenceText>
					{tMipManage('automated_emails.configuration.cadence.send_first')}
				</CadenceText>
				<NumberInput
					aria-label="Initial delay in days"
					name={CONFIG_CARD_FORM_FIELDS.initialDelayDays}
					control={control}
					min={0}
					disabled={!enabled}
					w={80}
				/>
				<CadenceText>
					{tMipManage('automated_emails.configuration.cadence.days_then_repeat')}
				</CadenceText>
				<NumberInput
					aria-label="Repeat every days"
					name={CONFIG_CARD_FORM_FIELDS.intervalDays}
					control={control}
					min={1}
					disabled={!enabled}
					w={80}
				/>
				<CadenceText>
					{tMipManage('automated_emails.configuration.cadence.days_max')}
				</CadenceText>
				<NumberInput
					aria-label="Maximum number of reminders"
					name={CONFIG_CARD_FORM_FIELDS.maxFires}
					control={control}
					min={1}
					disabled={!enabled}
					w={80}
				/>
				<CadenceText>
					{tMipManage('automated_emails.configuration.cadence.reminders')}
				</CadenceText>
			</CadenceFieldsRow>

			<PreviewLine>{previewLine}</PreviewLine>

			{showEndDate && (
				<DateInput
					label={tMipManage('automated_emails.configuration.end_date.label')}
					aria-label="End date"
					name={CONFIG_CARD_FORM_FIELDS.endDate}
					value={endDate}
					onChange={(value) =>
						setValue(CONFIG_CARD_FORM_FIELDS.endDate, value, { shouldDirty: true })
					}
					minDate={new Date()}
					clearable
					disabled={!enabled}
					popoverProps={{ withinPortal: true, zIndex: 1000 }}
				/>
			)}

			<ConfigCardFooter>
				<Button onClick={onSubmit} loading={isSaving} disabled={isSaving}>
					{tMipManage('automated_emails.configuration.save')}
				</Button>
			</ConfigCardFooter>
		</StyledConfigCard>
	);
};
